import { Box, Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';
import type { NewSubsidiary, SubsidiaryInput } from '@vy/types';
import { countries } from 'countries-list';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CountrySelect } from '../../../../../../../../components/new-design/inputs/CountrySelect';

type AccountInfoTabContentProps = {
  primarySelectable?: boolean;
};

export const AccountInfoTabContent: React.FC<AccountInfoTabContentProps> = ({
  primarySelectable = false,
}) => {
  const {
    control,
    register,
    formState: { defaultValues },
  } = useFormContext<SubsidiaryInput, unknown, NewSubsidiary>();

  const registerName = register('name');
  const registerTaxId = register('taxId');
  const registerStreetAddress = register('settings.address.addressText');
  const registerCity = register('settings.address.city');
  const registerState = register('settings.address.state');
  const registerPostalCode = register('settings.address.postalCode');
  const registerCountry = register('settings.address.country');

  return (
    <Stack direction="column" sx={{
      rowGap: "12px"
    }}>
      <Stack direction="row">
        <Controller
          control={control}
          name={registerName.name}
          defaultValue={defaultValues?.name ?? ''}
          render={({ field }) => <TextField size="small" fullWidth label="Name" {...field} />}
        />
        <div className="items-center flex">
          <Controller
            name="isPrimary"
            control={control}
            disabled={!primarySelectable}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={value} />}
                slotProps={{
                  typography: {
                    variant: 'caption',
                    color: 'text.secondary',
                  },
                }}
                labelPlacement="top"
                label="Primary"
              />
            )}
          />
        </div>
      </Stack>
      <Controller
        control={control}
        name={registerTaxId.name}
        defaultValue={defaultValues?.taxId ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Tax ID" {...field} />}
      />
      <Controller
        control={control}
        name={registerStreetAddress.name}
        defaultValue={defaultValues?.settings?.address?.addressText ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Street address" {...field} />}
      />
      <Controller
        control={control}
        name={registerCity.name}
        defaultValue={defaultValues?.settings?.address?.city ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="City" {...field} />}
      />
      <Stack direction="row" sx={{
        justifyContent: "space-between"
      }}>
        <Box
          sx={{
            display: 'flex',
            flex: 8,
            columnGap: '16px',
          }}
        >
          <Controller
            control={control}
            name={registerState.name}
            defaultValue={defaultValues?.settings?.address?.state ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="State" {...field} />}
          />

          <Controller
            control={control}
            name={registerPostalCode.name}
            defaultValue={defaultValues?.settings?.address?.postalCode ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="Postal code" {...field} />}
          />
        </Box>
      </Stack>
      <Controller
        control={control}
        name={registerCountry.name}
        defaultValue={defaultValues?.settings?.address?.country ?? ''}
        render={({ field }) => (
          <CountrySelect
            value={getCountryByName(field.value ?? '')}
            textFieldProps={{
              ...field,
              label: 'Country',
            }}
            onChange={(country) => {
              field.onChange(country?.name);
            }}
          />
        )}
      />
    </Stack>
  );
};

function getCountryByName(countryName: string) {
  return Object.values(countries).find((country) => country.name === countryName) ?? null;
}
